import { isLocale, type Locale } from '@bb/shared';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { publicEventTypeQuery } from '@/features/public-booking/api';
import { BookingFlow } from '@/features/public-booking/booking-flow';
import { ApiRequestError } from '@/lib/api';
import { ensureNamespace } from '@/lib/i18n';

/**
 * The booking flow as a widget for a customer's own website, loaded in an
 * iframe by /embed.js. Chrome-less, sized to content, and it reports its
 * height to the parent page so the frame can grow with it.
 *
 * nginx allows framing for this path only; the rest of the app stays
 * `frame-ancestors 'none'`.
 */
const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Accepts "1c1c1c", "#1C1C1C" or "abc"; returns "#rrggbb" or undefined. */
function hexColour(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const m = HEX.exec(value.trim());
  if (!m?.[1]) return undefined;
  const h = m[1].length === 3 ? [...m[1]].map((c) => c + c).join('') : m[1];
  return `#${h.toLowerCase()}`;
}

/** Black or white text, whichever reads better on the given colour. */
function textOn(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  const lum = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
  return lum > 0.55 ? '#171717' : '#ffffff';
}

interface EmbedSearch {
  lang?: Locale;
  details?: '0' | '1';
  bg?: string;
  fg?: string;
  accent?: string;
}

export const Route = createFileRoute('/embed/$username/$slug')({
  validateSearch: (s: Record<string, unknown>): EmbedSearch => {
    const bg = hexColour(s.bg);
    const fg = hexColour(s.fg);
    const accent = hexColour(s.accent);
    return {
      ...(isLocale(s.lang) ? { lang: s.lang } : {}),
      ...(s.details === '0' || s.details === 0 || s.details === false
        ? { details: '0' }
        : s.details === '1' || s.details === 1 || s.details === true
          ? { details: '1' }
          : {}),
      ...(bg ? { bg } : {}),
      ...(fg ? { fg } : {}),
      ...(accent ? { accent } : {}),
    };
  },
  loader: async ({ context, params }) => {
    await ensureNamespace('booking');
    try {
      return await context.queryClient.ensureQueryData(
        publicEventTypeQuery(params.username, params.slug),
      );
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: EmbedPage,
  notFoundComponent: EmbedNotFound,
});

/**
 * Marks the document as embedded, applies any requested colours, and streams
 * its height to the parent frame.
 */
function useEmbedFrame(
  colours: { bg?: string | undefined; fg?: string | undefined; accent?: string | undefined } = {},
) {
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add('bb-embed');
    // The other shades are mixed from these three in styles.css.
    if (colours.bg) html.style.setProperty('--bb-bg', colours.bg);
    if (colours.fg) html.style.setProperty('--bb-fg', colours.fg);
    if (colours.accent) {
      html.style.setProperty('--bb-accent', colours.accent);
      html.style.setProperty('--bb-on-accent', textOn(colours.accent));
    }
    const target = document.getElementById('root') ?? document.body;
    const report = () => {
      if (window.parent === window) return;
      const height = Math.ceil(target.getBoundingClientRect().height);
      // Only a number crosses the boundary, so any parent origin may receive it.
      window.parent.postMessage({ type: 'bb:resize', height }, '*');
    };
    const observer = new ResizeObserver(report);
    observer.observe(target);
    report();
    return () => {
      observer.disconnect();
      html.classList.remove('bb-embed');
      for (const v of ['--bb-bg', '--bb-fg', '--bb-accent', '--bb-on-accent']) {
        html.style.removeProperty(v);
      }
    };
  }, [colours.bg, colours.fg, colours.accent]);
}

function EmbedPage() {
  const { username, slug } = Route.useParams();
  const search = Route.useSearch();
  const { host, eventType } = Route.useLoaderData();
  // Saved settings apply by default; query parameters override them, which is
  // what the editor's live preview uses.
  const saved = eventType.embed;
  useEmbedFrame({
    bg: search.bg ?? saved.bg ?? undefined,
    fg: search.fg ?? saved.fg ?? undefined,
    accent: search.accent ?? saved.accent ?? undefined,
  });
  const showDetails =
    search.details === '0' ? false : search.details === '1' ? true : saved.showDetails;
  return (
    // The flow draws its own surface; this is just room for focus rings at the frame edge.
    <div className="p-0.5">
      <BookingFlow
        username={username}
        slug={slug}
        host={host}
        eventType={eventType}
        layout="embed"
        showDetails={showDetails}
      />
    </div>
  );
}

function EmbedNotFound() {
  const { t } = useTranslation('common');
  useEmbedFrame();
  return <p className="p-4 text-sm text-neutral-600">{t('notFound.body')}</p>;
}
